import { getSize } from '@/utils/iterable/getSize';
import { api } from '@/utils/api';
import { pushCompleteRegistration } from '@/utils/tracking/dataLayer';

import { RESET_PASSWORD_STATUS } from '@/constants/auth';
import { AFFILIATE } from '@/constants/user';
import { ENDPOINT } from '@/constants/api';
import { MESSAGE } from '@/constants/notifications';
import { TRUE } from '@/constants';

import actions from '@/actions';

function* commitPasswordReset(reduction, formData) {
  const state = reduction.setIn(['auth', 'loading'], true);

  yield function effect(dispatch) {
    function onSuccess(response) {
      response.result === 'success'
        ? dispatch(actions.auth.commitPasswordReset.fulfilled())
        : dispatch(actions.auth.commitPasswordReset.rejected(response.error));
    }

    function onFail(error) {
      dispatch(actions.auth.commitPasswordReset.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/resetPassword/setPassword', payload: formData })
      .then(onSuccess, onFail);
  };

  return state.hasIn(['auth', 'resetPasswordStatus'])
    ? state
    : state.setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_REQUESTED);
}

function* commitPasswordResetFulfilled(reduction) {
  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_ACCEPTED)
    .deleteIn(['auth', 'loading']);
}

function* commitPasswordResetRejected(reduction, error) {
  console.error(error);

  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_REJECTED)
    .deleteIn(['auth', 'loading']);
}

function* createAccount(reduction, request) {
  yield function effect(dispatch) {
    function onSuccess(response) {
      if (response.result === 'success') {
        try {
          pushCompleteRegistration();
        } catch (e) {
          console.warn('Sign-up conversion push failed', e);
        }
        dispatch(actions.auth.signIn.fulfilled());

        if (reduction.getIn(['auth', 'affiliate'])) {
          dispatch(actions.user.setProfileValue({ name: AFFILIATE, value: TRUE }));
        }
      } else {
        dispatch(actions.auth.signIn.rejected(response.data));
      }
    }

    function onFail(error) {
      dispatch(actions.auth.signIn.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/createAccount', ...request })
      .then(onSuccess, onFail);
  };

  return reduction.setIn(['auth', 'loading'], true);
}

function* requestPasswordReset(reduction, formData) {
  const state = reduction.setIn(['auth', 'loading'], true);

  yield function effect(dispatch) {
    function onSuccess(response) {
      response.result === 'success'
        ? dispatch(actions.auth.requestPasswordReset.fulfilled())
        : dispatch(actions.auth.requestPasswordReset.rejected(response.error));
    }

    function onFail(error) {
      dispatch(actions.auth.requestPasswordReset.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/resetPassword/request', payload: formData })
      .then(onSuccess, onFail);
  };

  return state.hasIn(['auth', 'resetPasswordStatus'])
    ? state
    : state.setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_REQUESTED);
}

function* requestPasswordResetFulfilled(reduction) {
  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_ACCEPTED)
    .deleteIn(['auth', 'loading']);
}

function* requestPasswordResetRejected(reduction, error) {
  console.error(error);

  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_REJECTED)
    .deleteIn(['auth', 'loading']);
}

function* resetPasswordStatus(reduction, status) {
  return status
    ? reduction.setIn(['auth', 'resetPasswordStatus'], status)
    : reduction.deleteIn(['auth', 'resetPasswordStatus']);
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['auth', ...path]
    : ['auth', path];

  if (value !== undefined) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (getSize(state.getIn(fullPath)) === 0) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* setLoading(reduction, value) {
  const state = reduction.deleteIn(['auth', 'error']);

  return value
    ? state.setIn(['auth', 'loading'], value)
    : state.deleteIn(['auth', 'loading']);
}

function* signIn(reduction, formData) {
  yield function effect(dispatch) {
    const { email: username, password } = formData;

    function onSuccess(response) {
      if (response.result === 'success') {
        dispatch(actions.auth.signIn.fulfilled());
      } else {
        dispatch(actions.auth.signIn.rejected(response.data));
      }
    }

    function onFail(error) {
      dispatch(actions.auth.signIn.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/login', payload: { username, password }})
      .then(onSuccess, onFail);
  };

  return reduction.setIn(['auth', 'loading'], true);
}

function* signInFulfilled(reduction) {
  yield function effect(dispatch) {
    dispatch(actions.user.getUserData());
  };

  return reduction
    .deleteIn(['auth', 'affiliate'])
    .deleteIn(['auth', 'loading'])
    .deleteIn(['auth', 'referrer'])
    .setIn(['auth', 'loggedIn'], true);
}

function* signInRejected(reduction, error) {
  if (typeof error !== 'string') {
    console.error(error);
  }

  return reduction
    .deleteIn(['auth', 'loading'])
    .setIn(['auth', 'error'],
      typeof error === 'string'
        ? error || MESSAGE.FAIL.UNKNOWN
        : MESSAGE.FAIL.UNKNOWN
    );
}

const auth = {
  [actions.auth.commitPasswordReset]: commitPasswordReset,
  [actions.auth.commitPasswordReset.fulfilled]: commitPasswordResetFulfilled,
  [actions.auth.commitPasswordReset.rejected]: commitPasswordResetRejected,
  [actions.auth.createAccount]: createAccount,
  [actions.auth.requestPasswordReset]: requestPasswordReset,
  [actions.auth.requestPasswordReset.fulfilled]: requestPasswordResetFulfilled,
  [actions.auth.requestPasswordReset.rejected]: requestPasswordResetRejected,
  [actions.auth.resetPasswordStatus]: resetPasswordStatus,
  [actions.auth.setData]: setData,
  [actions.auth.setLoading]: setLoading,
  [actions.auth.signIn]: signIn,
  [actions.auth.signIn.fulfilled]: signInFulfilled,
  [actions.auth.signIn.rejected]: signInRejected,
};

export default auth;
